import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Alert, Box, Button, Grid, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import PageHeader from 'components/PageHeader';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQueryErrorMessage } from 'utils/httpError';

import { deleteLab, Lab, provisionLabCustomerBucket, restoreLab } from 'api/labs';
import { Permission } from 'interfaces/permissionOption';
import { useNavigateBackFromAdmin } from 'utils/adminNavigation';
import queryClient from 'utils/queryClient';
import { labsQueryKey, useLabs } from 'utils/queryHooks/useLabs';
import { usePermissions } from 'utils/usePermissions';
import { wrapAdminPage } from '../AdminPageLayout';
import { CreateLabDialog } from './CreateLabDialog';
import { DeleteLabDialog } from './DeleteLabDialog';
import { EditLabDialog } from './EditLabDialog';
import { LabsSearch } from './LabsSearch';
import { ManageLabsList } from './ManageLabsList';
import { CustomerBucketProvisioningStatus } from './types';

type LabView = 'active' | 'archived';

const ManageLabsPage: React.FC = () => {
  const navigateBack = useNavigateBackFromAdmin();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreateLab = hasPermission(Permission.CreateLab);
  const canEditLab = hasPermission(Permission.EditLab);
  const canDeleteLab = hasPermission(Permission.DeleteLab);
  const canQueryArchivedLabs = canDeleteLab;

  const { data: labs = [], isLoading } = useLabs({ includeArchived: canQueryArchivedLabs });

  const [labView, setLabView] = useState<LabView>('active');
  const [labSearchText, setLabSearchText] = useState('');
  const [editingLabId, setEditingLabId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deletingLab, setDeletingLab] = useState<Lab | null>(null);
  const [deletedLabId, setDeletedLabId] = useState<string | null>(null);
  const [restoreAlert, setRestoreAlert] = useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const [provisioningFeedback, setProvisioningFeedback] = useState<CustomerBucketProvisioningStatus | null>(null);

  const activeLabs = useMemo(() => labs.filter((lab) => !lab.deletedAt), [labs]);
  const archivedLabs = useMemo(() => labs.filter((lab) => Boolean(lab.deletedAt)), [labs]);

  const editingLab = useMemo(
    () => activeLabs.find((lab) => lab.id === editingLabId) ?? null,
    [activeLabs, editingLabId]
  );

  const isArchivedViewSelected = canQueryArchivedLabs && labView === 'archived';
  const labsInSelectedView = isArchivedViewSelected ? archivedLabs : activeLabs;

  const filteredLabs = useMemo(() => {
    const trimmedSearch = labSearchText.trim().toLowerCase();

    if (!trimmedSearch) {
      return labsInSelectedView;
    }

    return labsInSelectedView.filter(
      (lab) => lab.name.toLowerCase().includes(trimmedSearch) || lab.id.toLowerCase().includes(trimmedSearch)
    );
  }, [labSearchText, labsInSelectedView]);

  const deleteMutation = useMutation((labId: string) => deleteLab(labId), {
    onSuccess: () => {
      if (deletingLab) {
        setDeletedLabId(deletingLab.id);
      }
      setDeletingLab(null);
      queryClient.invalidateQueries(labsQueryKey);
    },
  });

  const reprovisionLabCustomerBucketMutation = useMutation((labId: string) => provisionLabCustomerBucket(labId), {
    onSuccess: (response, labId) => {
      setProvisioningFeedback({
        labId,
        severity: 'success',
        message: response.customerBucketName
          ? `Customer bucket provisioning completed successfully. Customer bucket: ${response.customerBucketName}`
          : undefined,
      });
      queryClient.invalidateQueries(labsQueryKey);
    },
    onError: (error, labId) => {
      setProvisioningFeedback({
        labId,
        severity: 'error',
        message: getQueryErrorMessage(error, 'Customer bucket provisioning failed.'),
      });
    },
  });

  const restoreMutation = useMutation((labId: string) => restoreLab(labId), {
    onSuccess: (restoredLab) => {
      setRestoreAlert({
        severity: 'success',
        message: `${restoredLab.name} restored successfully.`,
      });
      queryClient.invalidateQueries(labsQueryKey);
    },
    onError: (error) => {
      setRestoreAlert({
        severity: 'error',
        message: getQueryErrorMessage(error, 'Failed to restore lab.'),
      });
    },
  });

  const handleDeleteConfirm = () => {
    if (deletingLab) {
      deleteMutation.mutate(deletingLab.id);
    }
  };

  const handleDeleteCancel = () => {
    deleteMutation.reset();
    setDeletingLab(null);
  };

  const handleReprovisionLabCustomerBucket = (labId: string) => {
    setProvisioningFeedback(null);
    reprovisionLabCustomerBucketMutation.mutate(labId);
  };

  const handleRestore = (labId: string) => {
    setRestoreAlert(null);
    restoreMutation.mutate(labId);
  };

  const handleLabViewChange = (_event: React.SyntheticEvent, nextLabView: LabView) => {
    if (!nextLabView) {
      return;
    }

    setDeletingLab(null);
    setEditingLabId(null);
    setLabView(nextLabView);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2 }}>
        <Grid container direction="column" spacing={3}>
          <PageHeader
            pageTitle="Manage Labs"
            pageSubtitle={
              <Typography variant="body2" color="text.secondary">
                Create, edit, archive, and restore labs. Manage bucket provisioning status.
              </Typography>
            }
            leadingAction={
              <Tooltip title="Back" placement="top" arrow>
                <IconButton aria-label="Back" onClick={navigateBack} sx={{ mr: 2 }}>
                  <ArrowBackRoundedIcon />
                </IconButton>
              </Tooltip>
            }
          />

          {canCreateLab && (
            <Grid item>
              <Button
                variant="contained"
                onClick={() => setIsCreateDialogOpen(true)}
                data-testid="create-lab-open-button"
              >
                Create Lab
              </Button>
            </Grid>
          )}

          {deletedLabId && (
            <Grid item>
              <Alert severity="success" data-testid="delete-lab-success-alert" onClose={() => setDeletedLabId(null)}>
                Lab deleted successfully.
              </Alert>
            </Grid>
          )}

          {restoreAlert && (
            <Grid item>
              <Alert
                severity={restoreAlert.severity}
                data-testid="restore-lab-alert"
                onClose={() => setRestoreAlert(null)}
              >
                {restoreAlert.message}
              </Alert>
            </Grid>
          )}

          {canQueryArchivedLabs && (
            <Grid item>
              <Tabs value={labView} onChange={handleLabViewChange} aria-label="lab views">
                <Tab label={`Active (${activeLabs.length})`} value="active" data-testid="active-labs-tab" />
                <Tab label={`Archived (${archivedLabs.length})`} value="archived" data-testid="archived-labs-tab" />
              </Tabs>
            </Grid>
          )}

          <LabsSearch
            labSearchText={labSearchText}
            onClearSearch={() => setLabSearchText('')}
            onSearchChange={setLabSearchText}
          />
          <ManageLabsList
            canCreateLab={canCreateLab}
            canDeleteLab={canDeleteLab}
            canEditLab={canEditLab}
            emptyStateMessage={isArchivedViewSelected ? 'No archived labs found.' : 'No active labs found.'}
            filteredLabs={filteredLabs}
            isLoading={isLoading}
            labSearchText={labSearchText}
            onReprovisionLabCustomerBucket={handleReprovisionLabCustomerBucket}
            onCloseProvisioningFeedback={() => setProvisioningFeedback(null)}
            onDelete={setDeletingLab}
            onEdit={(labId) => setEditingLabId((prev) => (prev === labId ? null : labId))}
            onNavigateToStudies={(labId) => navigate(`/?labId=${labId}`)}
            onRestore={handleRestore}
            provisioningFeedback={provisioningFeedback}
            reprovisioningLabCustomerBucketId={
              reprovisionLabCustomerBucketMutation.isLoading
                ? reprovisionLabCustomerBucketMutation.variables
                : undefined
            }
            restoringLabId={restoreMutation.isLoading ? restoreMutation.variables : undefined}
          />
        </Grid>

        <DeleteLabDialog
          deletingLab={deletingLab}
          isDeleting={deleteMutation.isLoading}
          isError={deleteMutation.isError}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
        <CreateLabDialog
          existingLabs={activeLabs}
          onClose={() => setIsCreateDialogOpen(false)}
          open={isCreateDialogOpen}
        />
        <EditLabDialog
          lab={editingLab}
          otherLabs={editingLab ? activeLabs.filter((candidateLab) => candidateLab.id !== editingLab.id) : []}
          onClose={() => setEditingLabId(null)}
        />
      </Box>
    </Box>
  );
};

export const ManageLabs = wrapAdminPage(ManageLabsPage);
